import React, { useMemo, useEffect, useState, useCallback, memo } from 'react'
import { useTable, useFilters, useGlobalFilter, usePagination, useFlexLayout, useExpanded } from 'react-table';
import GlobalSearch from '../table/GlobalSearch';
import TablePagination from '../table/TablePagination';
import { useDispatch } from 'react-redux';
import { fetchUserData } from '../../redux/slices/usersSlice';
import { useTranslation } from 'react-i18next';
import ExportExcel from '../ExcelExport';
import '../../css/userTable.css'
import UserSubrow from './UserSubrow'
import filterIcon from  '../../assets/icons/filter.svg';


const TableRow = memo(({ row, isExpanded }) => {

    return (
        <tr>
            {row.cells.map(cell => {
                return (
                    <td className={isExpanded ? 'expanded' : ''} key={cell.id} {...cell.getCellProps({
                        style: {
                            minWidth: cell.column.minWidth,
                            width: cell.column.width,
                        },
                    })}>
                        {cell.render('Cell')}
                    </td>
                )
            })}
        </tr>
    )
})

const UserTable = (props) => {
    const [data, setData] = useState(() => props.data);
    const columns = useMemo(() => props.columns, [props.columns]);
    const [ isFilterOpen, setIsFilterOpen ] = useState(false);
    const { t } = useTranslation();
    const [editableRow, setEditableRow] = useState(null);
    
    useEffect(() => {
        if (props.data) {
            setData(props.data);
        }
    }, [props.data]);
    
    const defaultColumn = useMemo(() => ({
        Filter: () => undefined,
        Cell: ({ value }) => <>{value?.length > 0 ? value : '--'}</>, 
        width: 150,
        minWidth: 20,
        maxWidth: 250,
    }), []); 

    const dispatch = useDispatch();
    
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        rows,
        prepareRow,
        state: { expanded, pageSize, pageIndex, ...state },
        preGlobalFilteredRows,
        setGlobalFilter,
        visibleColumns,
        ...paginationProps
    } = useTable({
        columns,
        data,
        defaultColumn,
        initialState: { pageIndex: 0, pageSize: 25},
    }, useFilters, useGlobalFilter, useFlexLayout, useExpanded, usePagination);

    const renderSearch = useCallback(() => (
        <GlobalSearch 
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
    ), [preGlobalFilteredRows, setGlobalFilter, state.globalFilter])
    
    const renderPagination = useCallback(() => (
        <TablePagination
            pageIndex={pageIndex}
            rows={rows}
            pageSize={pageSize}
            {...paginationProps}
        />
    ),[pageIndex, rows, pageSize, paginationProps])

    return (
        <>
            <div className='table-menu'>
                {renderSearch()}
                <div className='buttons'>
                    <button className='filter-btn' onClick={() => setIsFilterOpen(!isFilterOpen)}>
                        <img src={filterIcon} alt='filter icon'/>
                        <span>{t("filter.filter")}</span>
                    </button>
                    <ExportExcel excelData={data} fileName={"User Data"} />
                </div>
            </div>
            <div className={`filters-section ${isFilterOpen ? 'expanded' : ''}`}  >
                {headerGroups.map(headerGroup => (
                    headerGroup.headers.map((column, index) => (
                        <React.Fragment key={index}>
                            {column.canFilter && (
                                <div key={column.id} className='filter-container'>
                                    <span>{t(column.Header)}</span>
                                    {column.render('Filter')}
                                </div>
                            )}
                        </React.Fragment>
                    ))
                ))}
            </div>
            <table {...getTableProps()}>
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr  key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th key={column.id} {...column.getHeaderProps()} >
                                    {t(column.render('Header'))}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row)
                        row.getRowProps()
                        const{ isExpanded } = row;
                        return (
                            <React.Fragment key={row.id} >
                                <TableRow row={row} isExpanded={isExpanded} />
                                <UserSubrow row={row} isExpanded={isExpanded} />
                            </React.Fragment> 
                        )
                    })}
                </tbody>
            </table>
            <div className='table-footer'>
                {renderPagination()}
            </div>
        </>
    )
}

export default UserTable
